<script setup>
/**
 * pages/promotions/index.vue — Addendum 07, Modul S
 * ─────────────────────────────────────────────────────────
 * CRUD promo/diskon. auto_apply & pemakaian sesungguhnya saat checkout
 * (Modul V) belum dikerjakan di modul ini — halaman ini murni kelola data.
 */
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'
import { formatRupiah, formatDateOnly } from '@/composables/useFormat'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Promotion',
  },
})

const ability = useAbility()
const { loading, errors, request } = useApiRequest()

// -----------------------------------------------------------------------
// State
// -----------------------------------------------------------------------
const promotions = ref([])
const search = ref('')
const activeFilter = ref(null) // null=semua, true, false
const isFetching = ref(false)
const pagination = ref({ total: 0, current_page: 1, last_page: 1 })

const products = ref([])
const categories = ref([])

const dialog = ref(false)
const dialogMode = ref('create')
const dialogLoading = ref(false)
const deleteDialog = ref(false)
const selectedItem = ref(null)

const typeOptions = [
  { title: 'Persentase (%)', value: 'percentage' },
  { title: 'Nominal (Rp)', value: 'fixed_amount' },
]

const scopeOptions = [
  { title: 'Seluruh Order', value: 'order' },
  { title: 'Produk Tertentu', value: 'product' },
  { title: 'Kategori Tertentu', value: 'category' },
]

// 0=Minggu..6=Sabtu — SAMA persis dengan Carbon::dayOfWeek di backend.
const dayOptions = [
  { title: 'Minggu', value: 0 },
  { title: 'Senin', value: 1 },
  { title: 'Selasa', value: 2 },
  { title: 'Rabu', value: 3 },
  { title: 'Kamis', value: 4 },
  { title: 'Jumat', value: 5 },
  { title: 'Sabtu', value: 6 },
]

const defaultForm = () => ({
  name: '',
  description: '',
  type: 'percentage',
  value: null,
  scope: 'order',
  target_ids: [],
  min_purchase_amount: null,
  max_discount_amount: null,
  starts_at: null,
  ends_at: null,
  active_time_start: null,
  active_time_end: null,
  active_days: [],
  auto_apply: false,
  is_active: true,
})

const form = ref(defaultForm())
const formRef = ref()

const headers = [
  { title: 'Nama Promo', key: 'name' },
  { title: 'Tipe & Nilai', key: 'value', sortable: false },
  { title: 'Scope', key: 'scope', sortable: false },
  { title: 'Periode', key: 'period', sortable: false },
  { title: 'Auto-Apply', key: 'auto_apply', width: '110px' },
  { title: 'Status', key: 'is_active', width: '100px' },
  { title: 'Aksi', key: 'actions', sortable: false, width: '100px', align: 'end' },
]

const targetOptionsForScope = computed(() => {
  if (form.value.scope === 'product') return products.value
  if (form.value.scope === 'category') return categories.value

  return []
})

// -----------------------------------------------------------------------
// Fetch
// -----------------------------------------------------------------------
const fetchPromotions = async (page = 1) => {
  isFetching.value = true
  try {
    const params = { page }
    if (search.value) params.search = search.value
    if (activeFilter.value !== null) params.is_active = activeFilter.value ? 1 : 0

    const response = await useApi('/api/v1/promotions', { query: params }).get()
    const data = response?.data ?? response

    promotions.value = data?.data ?? []
    pagination.value = {
      total: data?.pagination?.total ?? 0,
      current_page: data?.pagination?.current_page ?? 1,
      last_page: data?.pagination?.last_page ?? 1,
    }
  } finally {
    isFetching.value = false
  }
}

const fetchTargetOptions = async () => {
  const [productsRes, categoriesRes] = await Promise.all([
    useApi('/api/v1/products', { query: { per_page: 200, is_active: 1 } }).get(),
    useApi('/api/v1/categories').get(),
  ])

  const productsData = productsRes?.data ?? productsRes
  const categoriesData = categoriesRes?.data ?? categoriesRes

  products.value = productsData?.data ?? []
  categories.value = Array.isArray(categoriesData) ? categoriesData : (categoriesData?.data ?? [])
}

// -----------------------------------------------------------------------
// Dialog helpers
// -----------------------------------------------------------------------
const openCreate = () => {
  dialogMode.value = 'create'
  form.value = defaultForm()
  formRef.value?.reset()
  dialog.value = true
}

const openEdit = item => {
  dialogMode.value = 'edit'
  selectedItem.value = item
  form.value = {
    name: item.name,
    description: item.description,
    type: item.type,
    value: item.value,
    scope: item.scope,
    target_ids: item.target_ids ?? [],
    min_purchase_amount: item.min_purchase_amount,
    max_discount_amount: item.max_discount_amount,
    starts_at: item.starts_at ? item.starts_at.slice(0, 10) : null,
    ends_at: item.ends_at ? item.ends_at.slice(0, 10) : null,
    active_time_start: item.active_time_start ? item.active_time_start.slice(0, 5) : null,
    active_time_end: item.active_time_end ? item.active_time_end.slice(0, 5) : null,
    active_days: item.active_days ?? [],
    auto_apply: item.auto_apply,
    is_active: item.is_active,
  }
  dialog.value = true
}

const confirmDelete = item => {
  selectedItem.value = item
  deleteDialog.value = true
}

// scope diganti -> target_ids lama (produk) tidak relevan lagi kalau pindah
// ke kategori (atau sebaliknya), dan tidak relevan sama sekali kalau ke 'order'.
watch(() => form.value.scope, () => {
  form.value.target_ids = []
})

// -----------------------------------------------------------------------
// CRUD
// -----------------------------------------------------------------------
const submitForm = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid) return

  dialogLoading.value = true

  const payload = {
    ...form.value,
    target_ids: form.value.scope === 'order' ? null : form.value.target_ids,
  }

  const res = await request(async () => {
    if (dialogMode.value === 'create') {
      return await useApi('/api/v1/promotions').post({ body: payload })
    } else {
      return await useApi(`/api/v1/promotions/${selectedItem.value.id}`).put({ body: payload })
    }
  })

  dialogLoading.value = false

  if (res) {
    dialog.value = false
    await fetchPromotions(pagination.value.current_page)
  }
}

const deletePromotion = async () => {
  const res = await request(() =>
    useApi(`/api/v1/promotions/${selectedItem.value.id}`).delete(),
  )

  if (res !== null) {
    deleteDialog.value = false
    await fetchPromotions(pagination.value.current_page)
  }
}

// -----------------------------------------------------------------------
// Display helpers
// -----------------------------------------------------------------------
const valueLabel = item => item.type === 'percentage'
  ? `${item.value}%`
  : formatRupiah(item.value)

const scopeLabel = item => {
  if (item.scope === 'order') return 'Seluruh Order'

  const count = item.target_ids?.length ?? 0

  return item.scope === 'product' ? `${count} Produk` : `${count} Kategori`
}

const periodLabel = item => {
  if (!item.starts_at && !item.ends_at) return 'Selalu aktif'

  const start = item.starts_at ? formatDateOnly(item.starts_at) : '...'
  const end = item.ends_at ? formatDateOnly(item.ends_at) : '...'

  return `${start} — ${end}`
}

// -----------------------------------------------------------------------
// Lifecycle
// -----------------------------------------------------------------------
onMounted(() => {
  fetchPromotions()
  fetchTargetOptions()
})

let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => fetchPromotions(1), 400)
})

watch(activeFilter, () => fetchPromotions(1))
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Promo & Diskon</h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Kelola promo persentase/nominal untuk seluruh order, produk, atau kategori tertentu.
        </p>
      </VCol>
    </VRow>

    <VCard>
      <VCardText class="d-flex align-center gap-3 flex-wrap">
        <VTextField
          v-model="search"
          placeholder="Cari nama promo..."
          prepend-inner-icon="ri-search-line"
          density="compact"
          hide-details
          clearable
          style="max-width: 280px"
        />
        <VSelect
          v-model="activeFilter"
          :items="[
            { title: 'Semua Status', value: null },
            { title: 'Aktif', value: true },
            { title: 'Nonaktif', value: false },
          ]"
          density="compact"
          hide-details
          style="max-width: 180px"
        />
        <VSpacer />
        <VBtn
          v-if="ability.can('create', 'Promotion')"
          color="primary"
          prepend-icon="ri-add-line"
          @click="openCreate"
        >
          Tambah Promo
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTable
        :headers="headers"
        :items="promotions"
        :loading="isFetching"
        hover
        no-data-text="Belum ada promo"
      >
        <template #item.value="{ item }">
          <span class="font-weight-medium">{{ valueLabel(item) }}</span>
          <div v-if="item.max_discount_amount" class="text-caption text-medium-emphasis">
            Maks. {{ formatRupiah(item.max_discount_amount) }}
          </div>
        </template>

        <template #item.scope="{ item }">
          <VChip size="small" variant="tonal" color="secondary">
            {{ scopeLabel(item) }}
          </VChip>
        </template>

        <template #item.period="{ item }">
          <span class="text-body-2">{{ periodLabel(item) }}</span>
          <div v-if="item.active_time_start" class="text-caption text-medium-emphasis">
            {{ item.active_time_start.slice(0, 5) }}–{{ item.active_time_end?.slice(0, 5) }}
          </div>
        </template>

        <template #item.auto_apply="{ item }">
          <VChip size="small" :color="item.auto_apply ? 'primary' : 'default'" variant="tonal">
            {{ item.auto_apply ? 'Otomatis' : 'Manual' }}
          </VChip>
        </template>

        <template #item.is_active="{ item }">
          <VChip size="small" :color="item.is_active ? 'success' : 'error'" variant="tonal">
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('update', 'Promotion')"
            icon size="small" variant="text" color="primary"
            @click="openEdit(item)"
          >
            <VIcon icon="ri-edit-line" />
            <VTooltip activator="parent">Edit</VTooltip>
          </VBtn>
          <VBtn
            v-if="ability.can('delete', 'Promotion')"
            icon size="small" variant="text" color="error"
            @click="confirmDelete(item)"
          >
            <VIcon icon="ri-delete-bin-line" />
            <VTooltip activator="parent">Hapus</VTooltip>
          </VBtn>
        </template>

        <template #no-data>
          <div class="text-center pa-8">
            <VIcon icon="ri-price-tag-3-line" size="64" color="grey-lighten-1" />
            <p class="text-body-1 mt-3 mb-1">Belum ada promo</p>
            <VBtn
              v-if="ability.can('create', 'Promotion')"
              color="primary" variant="tonal"
              @click="openCreate"
            >
              Tambah Promo Pertama
            </VBtn>
          </div>
        </template>
      </VDataTable>

      <VCardText v-if="pagination.last_page > 1" class="d-flex justify-center">
        <VPagination
          :model-value="pagination.current_page"
          :length="pagination.last_page"
          @update:model-value="fetchPromotions"
        />
      </VCardText>
    </VCard>

    <!-- ================================================================
         Dialog: Create / Edit
    ================================================================ -->
    <VDialog v-model="dialog" max-width="640" persistent>
      <VCard>
        <VCardTitle class="pa-4 pb-0 d-flex align-center justify-space-between">
          <span>{{ dialogMode === 'create' ? 'Tambah Promo' : 'Edit Promo' }}</span>
          <VBtn icon size="small" variant="text" @click="dialog = false">
            <VIcon icon="ri-close-line" />
          </VBtn>
        </VCardTitle>

        <VForm ref="formRef" @submit.prevent="submitForm">
          <VCardText class="pt-3">
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="form.name"
                  label="Nama Promo"
                  placeholder="Diskon Jam Ngantuk"
                  :rules="[requiredValidator]"
                  :error-messages="errors.name"
                  autofocus
                />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="form.description"
                  label="Deskripsi (opsional)"
                  rows="2"
                  auto-grow
                  :error-messages="errors.description"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VSelect
                  v-model="form.type"
                  :items="typeOptions"
                  label="Tipe Diskon"
                  :rules="[requiredValidator]"
                  :error-messages="errors.type"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.value"
                  :label="form.type === 'percentage' ? 'Nilai (%)' : 'Nilai (Rp)'"
                  type="number"
                  min="0"
                  :max="form.type === 'percentage' ? 100 : undefined"
                  step="any"
                  :rules="[requiredValidator]"
                  :error-messages="errors.value"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VSelect
                  v-model="form.scope"
                  :items="scopeOptions"
                  label="Berlaku Untuk"
                  :rules="[requiredValidator]"
                  :error-messages="errors.scope"
                />
              </VCol>
              <VCol v-if="form.scope !== 'order'" cols="12" sm="6">
                <VAutocomplete
                  v-model="form.target_ids"
                  :items="targetOptionsForScope"
                  item-title="name"
                  item-value="id"
                  :label="form.scope === 'product' ? 'Pilih Produk' : 'Pilih Kategori'"
                  multiple
                  chips
                  closable-chips
                  :rules="[v => (v && v.length > 0) || 'Pilih minimal 1']"
                  :error-messages="errors.target_ids"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.min_purchase_amount"
                  label="Min. Pembelian (opsional)"
                  type="number"
                  min="0"
                  step="any"
                  :error-messages="errors.min_purchase_amount"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.max_discount_amount"
                  label="Maks. Potongan (opsional)"
                  type="number"
                  min="0"
                  step="any"
                  hint="Batasi potongan maksimal — penting untuk tipe persentase"
                  persistent-hint
                  :error-messages="errors.max_discount_amount"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VTextField
                  v-model="form.starts_at"
                  label="Mulai (opsional)"
                  type="date"
                  :error-messages="errors.starts_at"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="form.ends_at"
                  label="Berakhir (opsional)"
                  type="date"
                  :error-messages="errors.ends_at"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VTextField
                  v-model="form.active_time_start"
                  label="Jam Mulai (opsional)"
                  type="time"
                  :error-messages="errors.active_time_start"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="form.active_time_end"
                  label="Jam Selesai (opsional)"
                  type="time"
                  :error-messages="errors.active_time_end"
                />
              </VCol>

              <VCol cols="12">
                <VSelect
                  v-model="form.active_days"
                  :items="dayOptions"
                  label="Hari Aktif (kosongkan = semua hari)"
                  multiple
                  chips
                  closable-chips
                  :error-messages="errors.active_days"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VSwitch
                  v-model="form.auto_apply"
                  label="Terapkan otomatis saat checkout"
                  color="primary"
                  density="compact"
                  hide-details
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VSwitch
                  v-model="form.is_active"
                  label="Aktif"
                  color="success"
                  density="compact"
                  hide-details
                />
              </VCol>
            </VRow>
          </VCardText>

          <VDivider />

          <VCardActions class="pa-4">
            <VSpacer />
            <VBtn variant="text" @click="dialog = false">Batal</VBtn>
            <VBtn type="submit" color="primary" :loading="dialogLoading">
              {{ dialogMode === 'create' ? 'Simpan' : 'Update' }}
            </VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>

    <!-- ================================================================
         Dialog: Delete
    ================================================================ -->
    <VDialog v-model="deleteDialog" max-width="440">
      <VCard>
        <VCardTitle class="text-h6 pa-4 pb-0">Hapus Promo?</VCardTitle>
        <VCardText class="pa-4 pt-2">
          Hapus promo <strong>{{ selectedItem?.name }}</strong>?
          Promo yang sudah pernah dipakai di transaksi akan dinonaktifkan saja, bukan dihapus.
        </VCardText>
        <VCardActions class="pa-4 pt-0">
          <VSpacer />
          <VBtn variant="text" @click="deleteDialog = false">Batal</VBtn>
          <VBtn color="error" :loading="loading" @click="deletePromotion">Hapus</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
